import { useParams, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { useActiveCompanyId } from '@/lib/active-company';
import { useMe } from '@/lib/auth';
import {
  type FollowUp,
  type ItemResponse,
  type ItemSummary,
  type ModuleSummary,
  type SubModuleSummary,
  useDeleteFollowUp,
  useModuleSummary,
  useResolveFollowUp,
  useUpsertFollowUp,
  useUpsertItemResponse,
} from '@/lib/assessments';
import { useUpdateCompany } from '@/lib/companies';
import { type ProfileQuestionKey } from '@/lib/profile-questions';
import { YesNo } from '@/components/YesNo';
import { cn } from '@/lib/cn';
import { useCanEdit } from '@/lib/can-edit';

type Verdict = 'YES' | 'NO' | 'NA' | null;

export function Module() {
  const { moduleId: moduleIdRaw } = useParams<{ moduleId: string }>();
  const moduleId = moduleIdRaw ? Number(moduleIdRaw) : undefined;
  const companyId = useActiveCompanyId();
  const summary = useModuleSummary(moduleId, companyId);

  if (!companyId)
    return (
      <Empty
        title="Select a company"
        body="Choose a company from the company switcher to start an assessment."
      />
    );
  if (summary.isLoading)
    return (
      <div className="mx-auto max-w-content-wide px-5 py-10 md:px-10 lg:px-20">
        <p className="text-body text-text-secondary">Loading module…</p>
      </div>
    );
  if (summary.isError || !summary.data)
    return (
      <Empty
        title="Module unavailable"
        body="We couldn't load this module. It may not exist or your subscription may not include it."
      />
    );

  return <ModuleView data={summary.data} companyId={companyId} />;
}

function ModuleView({ data, companyId }: { data: ModuleSummary; companyId: number }) {
  const sortedSubModules = useMemo(
    () =>
      [...data.subModules].sort((a, b) => a.displayOrder - b.displayOrder),
    [data.subModules],
  );

  const stats = useMemo(() => computeModuleStats(data), [data]);

  return (
    <div className="bg-background pb-10">
      <div className="mx-auto max-w-content-wide px-5 md:px-10 lg:px-20">
        <ModuleHeader data={data} stats={stats} />
        <ModuleProfileQuestions
          questions={data.questions}
          companyId={companyId}
        />
        <div className="mt-8 flex flex-col gap-12">
          {sortedSubModules.map((sm) => (
            <SubModuleSection
              key={sm.id}
              moduleCode={data.code}
              subModule={sm}
              moduleId={data.id}
              companyId={companyId}
              assessmentResponseId={data.assessmentResponseId}
            />
          ))}
        </div>
        <LegendFooter stats={stats} />
      </div>
    </div>
  );
}

function ModuleHeader({
  data,
  stats,
}: {
  data: ModuleSummary;
  stats: ModuleStats;
}) {
  const standardsCount = countStandards(data);
  return (
    <div className="flex flex-col gap-6 pt-8">
      <Breadcrumb items={['Dashboard', 'Modules', data.name]} />
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-start">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-sm bg-text-primary px-2.5 py-1 text-label font-bold uppercase tracking-[0.04em] text-white">
              {data.code}
            </span>
            <h1
              className="font-display font-bold text-text-primary"
              style={{
                fontSize: '28px',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {data.name}
            </h1>
          </div>
          <p className="text-body-sm text-text-secondary">
            {stats.totalItems} assessment item
            {stats.totalItems === 1 ? '' : 's'} · {standardsCount} standard
            {standardsCount === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex min-w-[200px] flex-col items-end gap-2">
          <div className="flex items-baseline gap-2">
            <span
              className="font-display font-bold text-accent"
              style={{
                fontSize: '28px',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {stats.compliancePct}%
            </span>
            <span className="text-[13px] text-text-secondary">compliant</span>
          </div>
          <div className="h-1.5 w-50 min-w-[200px] overflow-hidden rounded-sm bg-surface-tint">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${stats.compliancePct}%` }}
            />
          </div>
          <p className="text-caption text-text-secondary">
            {stats.compliant} of {stats.totalItems} items compliant
          </p>
        </div>
      </div>
    </div>
  );
}

function ModuleProfileQuestions({
  questions,
  companyId,
}: {
  questions: Record<string, string>;
  companyId: number;
}) {
  const entries = Object.entries(questions);
  const update = useUpdateCompany(companyId);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  if (entries.length === 0) return null;

  const answer = (key: string, value: boolean) => {
    setSavingKey(key);
    update.mutate(
      { [key as ProfileQuestionKey]: value },
      { onSettled: () => setSavingKey(null) },
    );
  };

  return (
    <div className="mt-8 rounded-lg border border-accent/30 bg-accent/5 p-6">
      <h2 className="text-body font-semibold text-text-primary">
        Confirm what applies to your company
      </h2>
      <p className="mt-1 text-body-sm text-text-secondary">
        These profile questions affect which items appear in this module. Answer
        them to tailor the list — your answers are saved to your company profile.
      </p>
      <ul className="mt-4 flex flex-col divide-y divide-accent/15">
        {entries.map(([key, text]) => (
          <li
            key={key}
            className="flex flex-col gap-3 py-4 first:pt-0 md:flex-row md:items-start md:justify-between md:gap-8"
          >
            <p className="text-body-sm text-text-primary">{text}</p>
            <div className="shrink-0">
              <YesNo
                label={text}
                value={null}
                disabled={update.isPending && savingKey === key}
                onChange={(value) => answer(key, value)}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Breadcrumb({ items }: { items: string[] }) {
  return (
    <p className="text-body-sm text-text-secondary">
      <Link to="/dashboard" className="hover:text-text-primary">
        {items[0]}
      </Link>
      {items.slice(1).map((label, i) => (
        <span key={i}>
          {' / '}
          {i === items.length - 2 ? (
            <span className="text-text-primary">{label}</span>
          ) : (
            <Link to="/dashboard" className="hover:text-text-primary">
              {label}
            </Link>
          )}
        </span>
      ))}
    </p>
  );
}

function SubModuleSection({
  moduleCode,
  subModule,
  moduleId,
  companyId,
  assessmentResponseId,
}: {
  moduleCode: string;
  subModule: SubModuleSummary;
  moduleId: number;
  companyId: number;
  assessmentResponseId: number;
}) {
  const total = subModule.assessmentItems.length;
  return (
    <section>
      <header className="flex items-baseline justify-between gap-4 border-b border-border pb-3">
        <h2 className="font-display text-[18px] font-semibold text-text-primary">
          Section {moduleCode}.{subModule.code}
        </h2>
        <p className="text-caption text-text-secondary">
          {total} item{total === 1 ? '' : 's'}
        </p>
      </header>
      {total === 0 ? (
        <p className="py-6 text-body-sm text-text-secondary">
          No applicable items in this section.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {subModule.assessmentItems.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              moduleCode={moduleCode}
              subModuleCode={subModule.code}
              moduleId={moduleId}
              companyId={companyId}
              assessmentResponseId={assessmentResponseId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ItemRow({
  item,
  moduleCode,
  subModuleCode,
  companyId,
  assessmentResponseId,
}: {
  item: ItemSummary;
  moduleCode: string;
  subModuleCode: string;
  moduleId: number;
  companyId: number;
  assessmentResponseId: number;
}) {
  void companyId;
  const me = useMe();
  const upsert = useUpsertItemResponse();
  // read_only members and lapsed companies see answers but cannot change them.
  const canEdit = useCanEdit();
  const response = item.assessmentItemResponses[0];
  const verdict = currentVerdict(response);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const setVerdict = (next: 'YES' | 'NO' | 'NA') => {
    upsert.mutate({
      assessmentResponseId: response?.assessmentResponseId ?? assessmentResponseId,
      assessmentItemId: item.id,
      meetsRequirement: next === 'YES' ? true : next === 'NO' ? false : null,
      notApplicable: next === 'NA' ? true : false,
      notes: response?.notes ?? '',
    });
  };

  const isDeficient = verdict === 'NO';
  const standardsCount = Object.values(item.standards).reduce(
    (acc, n) => acc + n,
    0,
  );
  const followUps = item.followUpItems;
  const primaryFollowUp = followUps[0];

  return (
    <li
      className={cn(
        'flex flex-col gap-3 py-6 md:flex-row md:gap-8',
        isDeficient && 'bg-danger/[0.03]',
      )}
    >
      <div className="flex grow flex-col gap-3 md:pr-8">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="text-label font-medium text-text-secondary">
            {moduleCode}.{subModuleCode}.{item.code}
          </span>
          <h3 className="text-[15px] font-semibold text-text-primary">
            {item.requirement}
          </h3>
          {isDeficient && (
            <span className="inline-flex items-center rounded-sm bg-danger px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-white">
              Deficient
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-caption">
          <span className="font-medium text-accent">
            {standardsCount} linked standard{standardsCount === 1 ? '' : 's'}
          </span>
          {followUps.length > 0 && (
            <span className="font-medium text-warning">
              {followUps.length} follow-up{followUps.length === 1 ? '' : 's'}
            </span>
          )}
          {canEdit ? (
            <button
              type="button"
              onClick={() => setFollowUpOpen((v) => !v)}
              className="font-medium text-text-secondary hover:text-text-primary"
            >
              {followUpOpen ? 'Cancel' : 'Add follow-up'}
            </button>
          ) : null}
        </div>
        {primaryFollowUp ? (
          <FollowUpStrip followUp={primaryFollowUp} canEdit={canEdit} />
        ) : null}
        {canEdit && followUpOpen && me.data ? (
          <NewFollowUpForm
            clientId={me.data.id}
            assessmentItemId={item.id}
            assessmentResponseId={response?.assessmentResponseId ?? assessmentResponseId}
            onSaved={() => setFollowUpOpen(false)}
          />
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-3 md:w-56">
        <div className="flex gap-2">
          <VerdictPill
            label="Yes"
            tone="success"
            selected={verdict === 'YES'}
            disabled={!canEdit}
            onClick={() => setVerdict('YES')}
          />
          <VerdictPill
            label="No"
            tone="danger"
            selected={verdict === 'NO'}
            disabled={!canEdit}
            onClick={() => setVerdict('NO')}
          />
          <VerdictPill
            label="N/A"
            tone="neutral"
            selected={verdict === 'NA'}
            disabled={!canEdit}
            onClick={() => setVerdict('NA')}
          />
        </div>
        <AnsweredCaption response={response} verdict={verdict} />
      </div>
    </li>
  );
}

function VerdictPill({
  label,
  tone,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  tone: 'success' | 'danger' | 'neutral';
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const selectedClasses = {
    success: 'border-success bg-success text-white',
    danger: 'border-danger bg-danger text-white',
    neutral: 'border-text-secondary bg-text-secondary text-white',
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 min-w-12 items-center justify-center rounded-md border px-4 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? selectedClasses[tone]
          : 'border-border bg-surface text-text-secondary hover:border-text-secondary',
      )}
      aria-pressed={selected}
    >
      {selected ? <span className="font-semibold">{label}</span> : label}
    </button>
  );
}

function AnsweredCaption({
  response,
  verdict,
}: {
  response: ItemResponse | undefined;
  verdict: Verdict;
}) {
  if (!response || !response.id) {
    return (
      <p className="text-[11px] text-warning">Not yet answered</p>
    );
  }
  const updatedAt = (response as ItemResponse & { updatedAt?: string })
    .updatedAt;
  const date = updatedAt ? formatShortDate(updatedAt) : null;
  if (verdict === 'NA') {
    return (
      <p className="text-[11px] text-text-secondary">
        Marked N/A{date ? ` on ${date}` : ''}
      </p>
    );
  }
  if (verdict === 'YES') {
    return (
      <p className="text-[11px] text-success">
        Answered{date ? ` ${date}` : ''}
      </p>
    );
  }
  if (verdict === 'NO') {
    return (
      <p className="text-[11px] text-danger">
        Answered{date ? ` ${date}` : ''}
      </p>
    );
  }
  return <p className="text-[11px] text-warning">Not yet answered</p>;
}

function FollowUpStrip({ followUp, canEdit }: { followUp: FollowUp; canEdit: boolean }) {
  const remove = useDeleteFollowUp();
  const resolve = useResolveFollowUp();
  const dueLabel = followUp.dueDate
    ? `Due ${formatShortDate(followUp.dueDate)}`
    : null;
  return (
    <div className="flex items-center gap-3 rounded-md border-l-[3px] border-warning bg-warning/10 px-4 py-3">
      <span className="text-[13px] font-medium text-text-primary">
        Follow-up:
      </span>
      <span className="grow text-[13px] text-text-secondary">
        {followUp.note}
        {dueLabel ? ` — ${dueLabel}` : ''}
      </span>
      {canEdit && (followUp.status === 'OPEN' || followUp.status === 'IN_PROGRESS') ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => resolve.mutate({ id: followUp.id })}
          disabled={resolve.isPending}
        >
          Resolve
        </Button>
      ) : null}
      {canEdit ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => remove.mutate(followUp.id)}
          disabled={remove.isPending}
        >
          Delete
        </Button>
      ) : null}
    </div>
  );
}

function NewFollowUpForm({
  clientId,
  assessmentItemId,
  assessmentResponseId,
  onSaved,
}: {
  clientId: number;
  assessmentItemId: number;
  assessmentResponseId: number | undefined;
  onSaved: () => void;
}) {
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState<
    'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  >('MEDIUM');
  const upsert = useUpsertFollowUp();

  const submit = () => {
    if (!note.trim() || !assessmentResponseId) return;
    upsert.mutate(
      {
        clientId,
        assessmentItemId,
        assessmentResponseId,
        note: note.trim(),
        severity,
      },
      {
        onSuccess: () => {
          setNote('');
          onSaved();
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface p-4">
      <textarea
        rows={2}
        placeholder="What needs to happen here?"
        className="rounded-md border border-border bg-surface p-3 text-body-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex items-center justify-between gap-2">
        <select
          className="h-9 rounded-md border border-border bg-surface px-2 text-body-sm"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as typeof severity)}
        >
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <Button
          size="sm"
          onClick={submit}
          disabled={upsert.isPending || !note.trim()}
        >
          {upsert.isPending ? 'Saving…' : 'Add follow-up'}
        </Button>
      </div>
    </div>
  );
}

function LegendFooter({ stats }: { stats: ModuleStats }) {
  return (
    <div className="mt-12 flex flex-col gap-3 border-t border-border pt-5 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-wrap items-center gap-6">
        <LegendDot color="bg-success" label={`${stats.compliant} Compliant`} />
        <LegendDot
          color="bg-danger"
          label={`${stats.nonCompliant} Non-Compliant`}
        />
        <LegendDot
          color="bg-border"
          label={`${stats.unanswered} Unanswered`}
        />
      </div>
      <p className="text-[13px] text-text-secondary">
        {stats.totalItems} item{stats.totalItems === 1 ? '' : 's'} across all
        sub-modules
      </p>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={cn('size-2.5 shrink-0 rounded-sm', color)} />
      <span className="text-[13px] font-medium text-text-primary">{label}</span>
    </span>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto max-w-content-narrow px-5 py-10 md:px-10 lg:px-20">
      <h1 className="font-display text-h1 font-bold">{title}</h1>
      <p className="mt-4 text-body text-text-secondary">{body}</p>
      <p className="mt-6">
        <Link to="/dashboard" className="text-accent hover:underline">
          Back to dashboard
        </Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ModuleStats {
  totalItems: number;
  compliant: number;
  nonCompliant: number;
  unanswered: number;
  compliancePct: number;
}

function computeModuleStats(data: ModuleSummary): ModuleStats {
  let totalItems = 0;
  let compliant = 0;
  let nonCompliant = 0;
  let unanswered = 0;
  for (const sm of data.subModules) {
    for (const item of sm.assessmentItems) {
      const response = item.assessmentItemResponses[0];
      if (response?.notApplicable) continue;
      totalItems += 1;
      if (!response || response.id == null) {
        unanswered += 1;
      } else if (response.meetsRequirement === true) {
        compliant += 1;
      } else if (response.meetsRequirement === false) {
        nonCompliant += 1;
      } else {
        unanswered += 1;
      }
    }
  }
  const compliancePct =
    totalItems > 0 ? Math.round((compliant / totalItems) * 100) : 0;
  return { totalItems, compliant, nonCompliant, unanswered, compliancePct };
}

function countStandards(data: ModuleSummary): number {
  let total = 0;
  for (const sm of data.subModules) {
    for (const item of sm.assessmentItems) {
      for (const count of Object.values(item.standards)) total += count;
    }
  }
  return total;
}

function currentVerdict(response: ItemResponse | undefined): Verdict {
  if (!response) return null;
  if (response.notApplicable) return 'NA';
  if (response.meetsRequirement === true) return 'YES';
  if (response.meetsRequirement === false) return 'NO';
  return null;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
